import React, { useState } from 'react';
import { FormGroup, Input, InputGroup, Label } from 'reactstrap';

import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import isNumber from 'services/numbers/isNumber';
import numericStringToFloat from 'services/numbers/numericStringToFloat';

import getCalculatingInputHandlers from './getCalculatingInputHandlers';

import 'style/layout/form/NumberWithDropdownInputField.css';

export default ({
  bsSize,
  className,
  disabled,
  displayDecimals,
  dropdownDisabled,
  dropdownName,
  dropdownOnChange,
  dropdownOptions,
  dropdownValue,
  id,
  label,
  labelClassName,
  maxIntegerDigits,
  numberDisabled,
  numberName,
  numberOnChange,
  numberValue,
  required,
}: {
  bsSize?: 'lg' | 'sm';
  className?: string;
  disabled?: boolean;
  // Shown with this many decimals while the field is not being edited (e.g. 1,7595 as
  // 1,76), so a column of prices lines up; the exact value shows when the field is edited
  // and is the one saved
  displayDecimals?: number;
  dropdownDisabled?: boolean;
  dropdownName?: string;
  dropdownOnChange: any;
  dropdownOptions: JSX.Element[];
  dropdownValue: string;
  id?: string;
  label?: string;
  labelClassName?: string;
  // Typing more digits before the decimals than this is ignored
  maxIntegerDigits?: number;
  numberDisabled?: boolean;
  numberName?: string;
  numberOnChange: any;
  numberValue: string;
  required: boolean;
}): JSX.Element => {
  const [editing, setEditing] = useState(false);
  // The value as it is shown when the field is entered, until something is typed: with
  // at least the displayed decimals (21 as 21,00), so the number does not jump
  const [enteredValue, setEnteredValue] = useState<null | string>(null);

  const withDisplayDecimals = (value: string): string => {
    const separator = value.includes('.') ? '.' : ',';
    const decimals = value.split(/[.,]/)[1] ?? '';
    return displayDecimals === undefined || decimals.length >= displayDecimals
      ? value
      : `${decimals === '' ? `${value}${separator}` : value}${'0'.repeat(
          displayDecimals - decimals.length
        )}`;
  };

  const shownValue = (() => {
    if (
      displayDecimals === undefined ||
      numberValue === '' ||
      !isNumber(numberValue)
    ) {
      return numberValue;
    }

    if (editing) {
      return enteredValue ?? numberValue;
    }

    return formatGroupedNumber(
      numericStringToFloat(numberValue),
      displayDecimals,
      displayDecimals
    );
  })();

  const handlers = getCalculatingInputHandlers({
    disabled: disabled || numberDisabled,
    maxIntegerDigits,
    name: numberName,
    onBlur: () => {
      setEditing(false);
      setEnteredValue(null);
    },
    onChange: numberOnChange,
    value: numberValue,
  });

  return (
    <FormGroup className={`${labelClassName} number-with-dropdown-input-field`}>
      {label === undefined ? null : (
        <Label className="label" for={id}>
          {label}
        </Label>
      )}
      <InputGroup>
        <Input
          bsSize={bsSize}
          className={`${className} text-field`}
          disabled={disabled || numberDisabled}
          id={id}
          invalid={!isNumber(numberValue)}
          name={numberName}
          onFocus={() => {
            setEditing(true);
            setEnteredValue(
              numberValue !== '' && isNumber(numberValue)
                ? withDisplayDecimals(numberValue)
                : null
            );
          }}
          // A calculation typed into the field (e.g. 2+2) is replaced with its result (4)
          onBlur={handlers.onBlur}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setEnteredValue(null);
            handlers.onChange(event);
          }}
          onKeyDown={handlers.onKeyDown}
          required={required}
          type="text"
          value={shownValue}
        />
        <Input
          bsSize={bsSize}
          className={`${className} text-field`}
          disabled={disabled || dropdownDisabled}
          name={dropdownName}
          onChange={dropdownOnChange}
          required={required}
          type="select"
          value={dropdownValue}
        >
          {dropdownOptions}
        </Input>
      </InputGroup>
    </FormGroup>
  );
};
